import { Notification } from "../../model/Notifications";
import { RootState } from "../../app/store";
import { deleteNotificationAsync, getNotificationAsync, viewNotificationAsync } from "./notificationsApi";
import { compareNotificationDates, findItemIndexById, insertItemAtIndex, removeItemAtIndex } from "../../utils/arrayUtils";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setDisplayFilter } from "../partyEvents/partyEventsSlice";

export interface NotificationState {
    notifications : Notification[]
    filteredNotifications: Notification[]
    status : 'idle' | 'loading' | 'failed'
    retrivedFromServer : boolean
    newNotificationReceived : boolean
    displayFilter : boolean
    filter : string
    sortDescending : boolean
    refreshIsRequired : boolean 
}
const initialState : NotificationState = {
    notifications: [],
    filteredNotifications: [],
    status: 'idle',
    retrivedFromServer : false ,
    newNotificationReceived : false,
    displayFilter : false,
    filter : '',
    sortDescending : false,
    refreshIsRequired: true
}
export interface GetNotificationRequest {
    email : string,
    token : string
}
export interface DeleteNotificationRequest {
    notificationId : number
    token: string
    email: string 
}
export interface DeleteNotificationResponse {
    notificationId: number
    success: boolean
}
export interface ViewedNotificationResponse {
    notificationId: number
    email: string
    success : boolean
}
export interface ViewedNotificationRequest {
    notificationId : number
    token: string
    email: string 
}
// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.

export const loadNotificationAsync = createAsyncThunk (
    'notifications/FetchNotifications' ,
    async (request : GetNotificationRequest) => {
        const response = await getNotificationAsync(request.email,request.token)
        return response; 
    }
);

export const removeNotificationAsync = createAsyncThunk(
    'notifications/removeNotificationsAsync' ,
    async (request: DeleteNotificationRequest) => {
        const apiResponse = await deleteNotificationAsync(request.notificationId, request.email, request.token)
        const response : DeleteNotificationResponse = {
            notificationId : request.notificationId,
            success : apiResponse
        }
        return response
    
    }
)

export const markAsViewedNotificationAsync = createAsyncThunk(
    'notifications/viewedNotificationAsync' ,
    async (request: ViewedNotificationRequest) => {
        const apiResponse = await viewNotificationAsync({ email: request.email, notificationId : request.notificationId}, request.token )
        const response : ViewedNotificationResponse = {
            notificationId : request.notificationId,
            email : request.email,
            success : apiResponse
        }
        return response
    }
)

export function addNotificationInOrder(notification : Notification, destination : Notification[]) {
  if (!notification.date) {
      destination.push(notification)
      return 
  }
  for (let i = 0; i<destination.length ; i ++) {
    if (notification.date < (destination[i].date ?? '0000-01-01T00:00:00') ) {
        destination = insertItemAtIndex(destination, notification, i)
        return
    }
  }
  destination.push(notification)
}

export function filterAndSortNotifications(originalList: Notification[], filter: string, sortDescending: boolean) {
  function sort (a : Notification, b : Notification) {
    return compareNotificationDates(a, b, sortDescending)
  }
  let filteredNotifications: Notification[] = []
    if (filter.trim().length==0) {
      filteredNotifications = originalList
    }
    else {
      let capitalFilter : string = filter.toUpperCase()
      originalList.map((notification) => {
        if ( notification.message.toUpperCase().indexOf(capitalFilter)>-1) {
          filteredNotifications.push(notification)
        }
      }
  ) }
  filteredNotifications.sort(sort)
   return filteredNotifications
}

export const notificationSlice = createSlice ({
    name: 'notification' , 
    initialState: initialState,
    reducers: {

        setNewNotificationReceived: (state, action: PayloadAction<boolean>) => {
            state.newNotificationReceived = action.payload
        },
        setNotificationsDisplayFilter : (state, action: PayloadAction<boolean>) => {
            state.displayFilter = action.payload
        },
        setNotificationsFilter: (state, action: PayloadAction<string> ) => {
        state.filter = action.payload
        state.filteredNotifications = filterAndSortNotifications(state.notifications, state.filter, state.sortDescending)
        },
        setNotificationSortDescending: (state, action: PayloadAction<boolean>) => {
        state.sortDescending = action.payload
        state.filteredNotifications = filterAndSortNotifications(state.notifications, state.filter, state.sortDescending)
        },
        setNewNotification: (state, action: PayloadAction<Notification>) => {
            // state.notifications = [...state.notifications, action.payload]
            state.notifications.push(action.payload)
            state.filteredNotifications = filterAndSortNotifications(state.notifications, state.filter, state.sortDescending)
        }
    },

    extraReducers: (builder) => {
        builder 
            .addCase(loadNotificationAsync.pending, (state) => {
                state.status = `loading` ;
            })
            .addCase(loadNotificationAsync.fulfilled, (state, action) => {
                state.retrivedFromServer = true
                state.refreshIsRequired = false
                state.status = `idle` ;
                state.notifications = action.payload;
                state.filter = '';
                state.filteredNotifications = filterAndSortNotifications(state.notifications, state.filter, state.sortDescending)
})
            .addCase(loadNotificationAsync.rejected, (state) => {
                state.status = `failed`
            })
            .addCase(removeNotificationAsync.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(removeNotificationAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                if (action.payload.success) {
                    const index = findItemIndexById(state.notifications, action.payload.notificationId)
                    state.notifications = removeItemAtIndex(state.notifications, index)
                    state.refreshIsRequired = true
                }
            })
            .addCase(removeNotificationAsync.rejected, (state) => { 
                state.status = 'failed';
            })
            .addCase(markAsViewedNotificationAsync.pending, (state) => {
                state.status = `loading`;
            })
            .addCase(markAsViewedNotificationAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                if (action.payload.success) {
                    const index = findItemIndexById(state.notifications, action.payload.notificationId)
                    state.notifications[index].viewed= true
                }
            })    
            .addCase(markAsViewedNotificationAsync.rejected, (state) => {
                state.status = 'failed';
            })

            
    }
})

export const selectNotifications = (state: RootState) => state.notification.notifications;
export const selectNotificationCount = (state: RootState) => state.notification.notifications.length;
export const selectNotificationRetrived = (state: RootState) => state.notification.retrivedFromServer;
export const selectNotificationStatus = (state: RootState) => state.notification.status;
export const selectNewNotificationReceived = (state: RootState) => state.notification.newNotificationReceived;
export const selectNotificationsDisplayFilter = (state: RootState) => state.notification.displayFilter
export const selectNotificationsFilter = (state: RootState) => state.notification.filter
export const selectFilteredNotifications = (state: RootState) => state.notification.filteredNotifications
export const selectNotificationSortDescending = (state: RootState) => state.notification.sortDescending
export const selectRefreshIsRequired = (state: RootState) => state.notification.refreshIsRequired

export const { setNewNotificationReceived, setNotificationsDisplayFilter, setNotificationsFilter, setNotificationSortDescending, setNewNotification} = notificationSlice.actions
export default notificationSlice.reducer;